import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useRef, useState } from 'react';
import { NativeSyntheticEvent } from 'react-native/Libraries/Types/CoreEventTypes';
import {
  TextInputContentSizeChangeEventData,
  TextInputKeyPressEventData,
} from 'react-native/Libraries/Components/TextInput/TextInput';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { Platform, TextInput } from 'react-native';

const getHeight = (height: number) => Math.min(Math.max(40, height), 400);

export interface ChatPromptInputProps {
  onChangeText?: (text: string) => void;
  value?: string;
  onSubmit?: () => void;
  /** Override the default (chat namespace) placeholder. */
  placeholder?: string;
  /** Multiline auto-grow (default). Set false for a single-line field. */
  multiline?: boolean;
  /** Block typing + Enter-to-send while the AI is thinking. */
  disabled?: boolean;
  /** Extra classes merged onto the input (e.g. a white background). */
  inputClassName?: string;
}

export const ChatPromptInput = ({
  onChangeText,
  value,
  onSubmit,
  placeholder,
  multiline = true,
  disabled,
  inputClassName,
}: ChatPromptInputProps) => {
  const { t } = useTranslation('chat');
  const [inputStyle, setInputStyle] = useState({ height: 40 });
  const handleContentSizeChange = useCallback(
    (e: NativeSyntheticEvent<TextInputContentSizeChangeEventData>) => {
      if (Platform.OS !== 'web' || !multiline) return;
      const contentHeight = e.nativeEvent.contentSize.height;
      setInputStyle((curr) => {
        const newStyle = {
          height: getHeight(contentHeight),
        };
        if (curr.height === newStyle.height) {
          return curr;
        }
        return newStyle;
      });
    },
    [],
  );
  const ref = useRef<TextInput>(null);
  useEffect(() => {
    const element = ref.current as unknown as HTMLElement;
    if (Platform.OS === 'web' && multiline && !!element) {
      window.requestAnimationFrame(() => {
        element.style.height = '0px';
        element.style.height = `${getHeight(element.scrollHeight)}px`;
      });
    }
  }, [value]);
  const handleKeyPress = useCallback(
    (e: NativeSyntheticEvent<TextInputKeyPressEventData>) => {
      if (Platform.OS !== 'web') return;
      if (
        e.nativeEvent.key === 'Enter' &&
        !(e.nativeEvent as unknown as { shiftKey: boolean }).shiftKey
      ) {
        e.preventDefault();
        if (disabled) return;
        onSubmit?.();
      }
    },
    [onSubmit, disabled],
  );
  return (
    <Input
      ref={ref}
      onChangeText={onChangeText}
      value={value}
      onKeyPress={handleKeyPress}
      onContentSizeChange={handleContentSizeChange}
      multiline={multiline}
      editable={!disabled}
      placeholder={placeholder ?? t('input.placeholder')}
      style={inputStyle}
      className={cn(
        'font-regular flex-1 rounded-full bg-surface border-transparent px-4',
        inputClassName,
      )}
    />
  );
};
